import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { geoApi, geoQueryKeys } from "../api/geo";
import { hisIntegrationApi, hisIntegrationQueryKeys } from "../api/hisIntegration";
import { SearchableSelect } from "./SearchableSelect";

/**
 * Reusable cascading Vietnam address picker (2-level: province + ward).
 *
 * <p>Values are stored/emitted as **strings**. By default (`source="geo"`)
 * they are Addy Geo's `national_code` (numeric on the wire, but Patient/
 * Branch persist it as varchar). Pass `source="his"` for a patient-address
 * picker on a tenant bound to an external HIS (ADR-0020): such a tenant uses
 * Addy only for booking (product decision, 2026-08-22), so the vocabulary is
 * that connector's OWN province/ward dictionary (`his_location_catalog`)
 * instead — the codes stored are the external HIS's own ids, pushed through
 * to a booking with no translation. A clinic's own address (branches) always
 * uses `source="geo"` regardless of HIS binding — it is never pushed to an
 * external HIS.
 *
 * <p>Selecting a province resets the ward. The ward select is disabled until
 * a province is chosen.
 */

const GEO_STALE = Number.POSITIVE_INFINITY;

export interface AddressValue {
  provinceCode: string | null;
  wardCode: string | null;
}

interface Props {
  provinceCode: string | null;
  wardCode: string | null;
  onChange: (v: AddressValue) => void;
  disabled?: boolean;
  /** "geo" (default) = Addy Geo national codes. "his" = the bound external HIS's own codes. */
  source?: "geo" | "his";
}

export function AddressPicker({
  provinceCode,
  wardCode,
  onChange,
  disabled,
  source = "geo",
}: Props) {
  const provincesQuery = useQuery({
    queryKey:
      source === "his" ? hisIntegrationQueryKeys.locationProvinces() : geoQueryKeys.provinces(),
    queryFn: async (): Promise<{ code: string; name: string }[]> =>
      source === "his"
        ? hisIntegrationApi.locationProvinces()
        : (await geoApi.provinces()).map((p) => ({ code: String(p.code), name: p.name })),
    staleTime: GEO_STALE,
    gcTime: GEO_STALE,
  });

  const wardsQuery = useQuery({
    queryKey:
      source === "his"
        ? hisIntegrationQueryKeys.locationWards(provinceCode)
        : geoQueryKeys.wards(provinceCode),
    queryFn: async (): Promise<{ code: string; name: string }[]> =>
      source === "his"
        ? hisIntegrationApi.locationWards(provinceCode as string)
        : (await geoApi.wards(provinceCode as string)).map((w) => ({
            code: String(w.code),
            name: w.name,
          })),
    enabled: !!provinceCode,
    staleTime: GEO_STALE,
    gcTime: GEO_STALE,
  });

  const provinceOptions = useMemo(
    () =>
      (provincesQuery.data ?? []).map((p) => ({
        value: String(p.code),
        label: p.name,
      })),
    [provincesQuery.data]
  );

  const wardOptions = useMemo(
    () =>
      (wardsQuery.data ?? []).map((w) => ({
        value: String(w.code),
        label: w.name,
      })),
    [wardsQuery.data]
  );

  return (
    <>
      <div className="grid gap-1.5 [&_label]:text-sm [&_label]:font-medium">
        <label htmlFor="addr-province">Tỉnh/Thành phố</label>
        <SearchableSelect
          ariaLabel="Tỉnh/Thành phố"
          value={provinceCode}
          options={provinceOptions}
          disabled={disabled}
          loading={provincesQuery.isLoading}
          placeholder="— Chọn tỉnh/thành —"
          searchPlaceholder="Tìm tỉnh/thành…"
          // Selecting a province resets the ward.
          onChange={(v) => onChange({ provinceCode: v, wardCode: null })}
        />
      </div>
      <div className="grid gap-1.5 [&_label]:text-sm [&_label]:font-medium">
        <label htmlFor="addr-ward">Phường/Xã</label>
        <SearchableSelect
          ariaLabel="Phường/Xã"
          value={wardCode}
          options={wardOptions}
          disabled={disabled || !provinceCode}
          loading={!!provinceCode && wardsQuery.isLoading}
          placeholder={
            provinceCode ? "— Chọn phường/xã —" : "— Chọn tỉnh/thành trước —"
          }
          searchPlaceholder="Tìm phường/xã…"
          onChange={(v) => onChange({ provinceCode, wardCode: v })}
        />
      </div>
    </>
  );
}
